import { useState, useEffect, useCallback, useRef } from "react";

// hook for managing resizable table height with drag handle
export const useTableResize = (initialHeight = 600, dataLength = 0) => {
  const [tableHeight, setTableHeight] = useState(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // calculate max height based on number of rows
  const ROW_HEIGHT = 51.4;
  const HEADER_HEIGHT = 56;
  const FOOTER_BUFFER = 18;
  const maxTableHeight =
    dataLength * ROW_HEIGHT + HEADER_HEIGHT + FOOTER_BUFFER;

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newHeight = e.clientY - containerRect.top;

      // constrain between min (200px) and max (all rows visible)
      const constrainedHeight = Math.max(
        200,
        Math.min(newHeight, maxTableHeight)
      );
      setTableHeight(constrainedHeight);
    },
    [isDragging, maxTableHeight]
  );

  // attach global mouse listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // toggle between initial height and max height
  const toggleTableExpansion = useCallback(() => {
    setTableHeight((prev) =>
      prev >= maxTableHeight - 20 ? initialHeight : maxTableHeight
    );
  }, [maxTableHeight, initialHeight]);

  return {
    tableHeight,
    setTableHeight,
    isDragging,
    containerRef,
    maxTableHeight,
    handleMouseDown,
    toggleTableExpansion,
  };
};
